/**
 * In-browser stand-in for the API, used only by the shareable preview build
 * (`npm run build:preview`). The production build never imports this — the
 * bundler drops it entirely when VITE_USE_MOCK is unset.
 *
 * It exists so the journey can be reviewed and shared without standing up a
 * database or spending getAddress.io credits. The components under review are
 * the real ones; only the network layer underneath them is faked.
 */

const JOURNEYS = {
  home: {
    slug: 'home',
    name: 'Home broadband — main',
    brand: 'home',
    config: {
      ctaLabel: 'Next Step',
    },
  },
  business: {
    slug: 'business',
    name: 'Business broadband — main',
    brand: 'business',
    config: {
      ctaLabel: 'Next Step',
    },
  },
  ggbb2: {
    slug: 'ggbb2',
    name: 'Google Ads — home broadband v2',
    brand: 'home',
    config: {
      ctaLabel: 'Next Step',
    },
  },
  ggbb3: {
    slug: 'ggbb3',
    name: 'Google Ads — home broadband v3',
    brand: 'home',
    config: {
      ctaLabel: 'Next Step',
    },
  },
};

/** A long list, so the filter box and scrolling behaviour can be reviewed too. */
function terrace(street, town, county, count, start = 1) {
  return Array.from({ length: count }, (_, i) => ({
    line_1: `${start + i} ${street}`,
    town_or_city: town,
    county,
  }));
}

const ADDRESSES = {
  SW1A2AA: [
    { line_1: '10 Downing Street', town_or_city: 'London', county: 'Greater London' },
    { line_1: '11 Downing Street', town_or_city: 'London', county: 'Greater London' },
    { line_1: '12 Downing Street', town_or_city: 'London', county: 'Greater London' },
  ],
  M11AE: [
    { line_1: 'Flat 1, The Hacienda', line_2: '11 Whitworth Street West', town_or_city: 'Manchester', county: 'Greater Manchester' },
    { line_1: 'Flat 2, The Hacienda', line_2: '11 Whitworth Street West', town_or_city: 'Manchester', county: 'Greater Manchester' },
    { line_1: 'Flat 3, The Hacienda', line_2: '11 Whitworth Street West', town_or_city: 'Manchester', county: 'Greater Manchester' },
    ...terrace('Whitworth Street West', 'Manchester', 'Greater Manchester', 9, 12),
  ],
  B338TH: terrace('Coventry Road', 'Birmingham', 'West Midlands', 24),
  LS16AE: terrace('Wellington Street', 'Leeds', 'West Yorkshire', 14),
};

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

const compact = (pc) => String(pc ?? '').replace(/\s+/g, '').toUpperCase();

const label = (a) =>
  [a.line_1, a.line_2, a.line_3, a.town_or_city].filter(Boolean).join(', ');

function hash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  return Math.abs(h).toString(16).padStart(12, '0').slice(0, 12);
}

export async function getJourney(slug) {
  await delay(120);
  const journey = JOURNEYS[String(slug).toLowerCase()];
  if (!journey) {
    const err = new Error(`Unknown journey "${slug}"`);
    err.status = 404;
    throw err;
  }
  return journey;
}

export async function lookupPostcode(postcode) {
  // Enough latency to show the button's loading state honestly.
  await delay(450);
  const key = compact(postcode);
  const list = ADDRESSES[key];

  if (!list) {
    // Any other valid-format postcode returns a small generic list, so the
    // reviewer is never dead-ended by typing their own postcode.
    const generic = terrace('High Street', 'Anytown', 'Anyshire', 6);
    return {
      postcode: `${key.slice(0, -3)} ${key.slice(-3)}`,
      count: generic.length,
      addresses: generic.map((a) => ({ id: hash(label(a)), label: label(a), ...a })),
    };
  }

  return {
    postcode: `${key.slice(0, -3)} ${key.slice(-3)}`,
    count: list.length,
    addresses: list.map((a) => ({ id: hash(label(a)), label: label(a), ...a })),
  };
}

export async function submitLead() {
  await delay(700);
  return { ok: true, reference: `PREVIEW-${hash(String(Date.now()))}` };
}

export function trackStep() {
  /* no-op in preview */
}

export const PREVIEW_JOURNEYS = Object.values(JOURNEYS).map((j) => ({
  slug: j.slug,
  name: j.name,
  brand: j.brand,
}));

/** Contact enquiries in the preview: accepted, stored nowhere. */
export async function submitEnquiry() {
  await delay(600);
  return { ok: true };
}
